import { Dialog } from '@headlessui/react';
import { useRef, useState } from 'react';
import { HiOutlineArrowPath, HiOutlineCheck, HiOutlineCircleStack, HiOutlineCloudArrowUp, HiOutlineDocumentText, HiOutlineGlobeAlt, HiOutlinePlus, HiOutlineTrash, HiOutlineXMark } from 'react-icons/hi2';
import { useAppInfo } from '../../../../helpers/hooks/common-hook';
import { modelsByProvider } from '../../../agent-manage/llm-settings';
import { dataSourceCreate, fileUpload, uploadUrlGet } from '../../../../services/knowledge/ds-connector/file-connector';
import { isValidURL, validateActionName } from '../../../../helpers/utils/text-util';

const SOURCE_TYPES = [
  { value: 'file', label: 'Document', description: 'Upload a file', icon: HiOutlineDocumentText },
  { value: 'database', label: 'Database', description: 'Connect your data', icon: HiOutlineCircleStack },
  { value: 'web', label: 'Website', description: 'Import web pages', icon: HiOutlineGlobeAlt },
];
const OCR_PROVIDERS = [
  { value: 'default', label: 'Default (LLM Vision)' },
  { value: 'openai', label: 'OpenAI Vision' },
  { value: 'gemini', label: 'Google Gemini Vision' },
];
const DOCUMENT_TYPES = ['pdf', 'doc', 'docx', 'csv', 'txt'];
const IMAGE_TYPES = ['png', 'jpg', 'jpeg', 'webp', 'tif', 'tiff', 'bmp'];
const inputClass = 'block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-gray-400 focus:ring-gray-400 read-only:bg-gray-50 read-only:text-gray-500';
const secondaryButton = 'inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-50';
const initialMetadata = {
  dataSourceId: '', dataSourceType: 'file', dataSourceUrl: '', dataType: 'text',
  idField: '', searchField: '', descriptionForModel: '', returnOriginal: false,
  ocr: { enable: false, provider: 'default' }, urls: [], scheduler: { rateType: '', value: '' },
};

function Field({ label, name, error, hint, children, ...props }) {
  return (
    <div className="min-w-0 space-y-2">
      <label htmlFor={name} className="block text-sm font-medium text-gray-800">{label}</label>
      {children || <input id={name} name={name} className={inputClass} aria-invalid={Boolean(error)} aria-describedby={error || hint ? `${name}-help` : undefined} {...props} />}
      {(error || hint) && <p id={`${name}-help`} className={`text-xs leading-5 ${error ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>{error || hint}</p>}
    </div>
  );
}

export default function FileDSConnector({ savedDataSet = initialMetadata, editMode = false, onCancel }) {
  const { appId } = useAppInfo();
  const [metadata, setMetadata] = useState(() => ({ ...initialMetadata, ...savedDataSet }));
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [progress, setProgress] = useState('');
  const [dragging, setDragging] = useState(false);
  const busy = useRef(false);
  const form = useRef(null);
  const processing = Boolean(progress);
  const sourceType = metadata.dataSourceType;
  const acceptedTypes = [...DOCUMENT_TYPES, ...(metadata.ocr?.enable ? IMAGE_TYPES : [])];
  const urls = metadata.urls?.length ? metadata.urls : [''];
  const update = (name, value) => {
    setMetadata(previous => ({ ...previous, [name]: value }));
    setErrors(previous => ({ ...previous, [name]: undefined }));
    setSaveError('');
  };
  const handleChange = event => update(event.target.name, event.target.value);
  const close = () => { if (!busy.current) onCancel(); };

  const selectFile = selected => {
    if (!selected || busy.current) return;
    const extension = selected.name.split('.').pop().toLowerCase();
    if (!acceptedTypes.includes(extension)) {
      setErrors(previous => ({ ...previous, file: 'Choose a supported file type. Enable OCR to upload an image.' }));
      return;
    }
    setFile(selected);
    update('dataType', extension);
    setErrors(previous => ({ ...previous, file: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!validateActionName(metadata.dataSourceId || '')) next.dataSourceId = 'Use letters, numbers, underscores or hyphens, without spaces.';
    if (sourceType === 'file') {
      if (!file && !editMode) next.file = 'Choose a document to upload.';
      if (file && !acceptedTypes.includes(file.name.split('.').pop().toLowerCase())) next.file = 'Enable OCR for this image, or choose a supported document.';
    }
    if (sourceType === 'database') {
      for (const [name, label] of [['dbType', 'database type'], ['dbHost', 'host'], ['dbName', 'database name']]) {
        if (!metadata[name]?.trim()) next[name] = `Enter a ${label}.`;
      }
      const port = Number(metadata.dbPort);
      if (!Number.isInteger(port) || port < 1 || port > 65535) next.dbPort = 'Enter a port from 1 to 65535.';
    }
    if (sourceType === 'web') {
      const populated = urls.map(url => url.trim()).filter(Boolean);
      if (!populated.length || !populated.every(url => /^https?:\/\//i.test(url) && isValidURL(url))) next.urls = 'Enter at least one valid HTTP or HTTPS URL.';
      if (metadata.scheduler?.rateType && (!['hourly', 'daily'].includes(metadata.scheduler.rateType) || !Number.isInteger(Number(metadata.scheduler.value)) || Number(metadata.scheduler.value) < 1)) next.schedule = 'Enter a whole number greater than zero.';
    }
    setErrors(next);
    if (Object.keys(next).length) {
      requestAnimationFrame(() => form.current?.querySelector('[aria-invalid="true"]')?.focus());
      return false;
    }
    return true;
  };

  const save = async (extract = false) => {
    if (busy.current || !validate()) return;
    busy.current = true;
    setSaveError('');
    setProgress('Saving data source…');
    try {
      let payload = { ...metadata };
      if (sourceType === 'file' && file) {
        setProgress('Preparing upload…');
        if (typeof payload.searchableFields === 'string') payload.searchableFields = payload.searchableFields.split(',');
        const result = await uploadUrlGet(appId, { fileExt: file.name.split('.').pop().toLowerCase(), metadata: payload });
        if (result.status !== 200 || !result.response?.uploadUrl || !result.response?.metadata) throw new Error('Unable to prepare the upload. Please try again.');
        setProgress('Uploading document…');
        const uploaded = await fileUpload(result.response.uploadUrl, file);
        if (!uploaded?.ok) throw new Error('The document could not be uploaded. Please try again.');
        payload = result.response.metadata;
      }
      if (sourceType === 'web') {
        payload.urls = urls.map(url => url.trim()).filter(Boolean);
        payload.manualExtract = extract;
        if (metadata.scheduler?.rateType) payload.scheduler = { ...metadata.scheduler, value: Number(metadata.scheduler.value) };
        else delete payload.scheduler;
      }
      setProgress(extract ? 'Starting extraction…' : 'Saving data source…');
      const result = await dataSourceCreate(appId, payload);
      if (!(result?.status >= 200 && result?.status < 300)) throw new Error('Unable to save this data source. Please try again.');
      onCancel(true);
    } catch (error) {
      setSaveError(error.message || 'Something went wrong. Please try again.');
    } finally {
      busy.current = false;
      setProgress('');
    }
  };

  return (
    <Dialog open onClose={close} initialFocus={form} className="relative z-50">
      <div className="fixed inset-0 bg-gray-950/45 backdrop-blur-sm" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-3 sm:p-6">
        <Dialog.Panel className="flex max-h-[calc(100dvh-3rem)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <header className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-100 px-6 py-5">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-100 text-gray-900 dark:text-brand-lime"><HiOutlineCircleStack className="h-5 w-5" /></span>
              <div><Dialog.Title className="text-lg font-semibold tracking-tight text-gray-900">{editMode ? 'Edit data source' : 'Add data source'}</Dialog.Title><Dialog.Description className="mt-1 text-sm text-gray-500">{editMode ? 'Update the information your agent learns from.' : 'Give your agent knowledge from documents, databases or the web.'}</Dialog.Description></div>
            </div>
            <button type="button" onClick={close} disabled={processing} aria-label="Close data source dialog" className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-40"><HiOutlineXMark className="h-5 w-5" /></button>
          </header>

          <form ref={form} tabIndex={-1} noValidate onSubmit={event => { event.preventDefault(); save(); }} className="flex min-h-0 flex-1 flex-col outline-none" aria-busy={processing}>
            <div className="min-h-0 overflow-y-auto px-6 py-6">
              {saveError && <div role="alert" className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">{saveError}</div>}
              <fieldset disabled={processing} className="min-w-0 space-y-6 disabled:opacity-70">
                <fieldset>
                  <legend className="mb-3 text-sm font-medium text-gray-800">Source type</legend>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {SOURCE_TYPES.map(({ value, label, description, icon: Icon }) => <label key={value} className={`relative flex items-center gap-3 rounded-xl border p-3 transition ${editMode ? 'cursor-default' : 'cursor-pointer'} ${sourceType === value ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900' : 'border-gray-200'} ${editMode && sourceType !== value ? 'opacity-40' : ''}`}>
                      <input type="radio" name="sourceType" value={value} checked={sourceType === value} disabled={editMode} onChange={() => {
                        setMetadata(previous => ({ ...previous, dataSourceType: value, dataType: value === 'web' ? 'web' : file?.name.split('.').pop().toLowerCase() || 'text' }));
                        setErrors({}); setSaveError('');
                      }} className="peer sr-only" />
                      <span className="pointer-events-none absolute inset-0 rounded-xl peer-focus-visible:ring-2 peer-focus-visible:ring-gray-400 peer-focus-visible:ring-offset-2" />
                      <Icon className="h-5 w-5 shrink-0 text-gray-500" />
                      <span><span className="block text-sm font-medium text-gray-900">{label}</span><span className="block text-xs text-gray-500">{description}</span></span>
                    </label>)}
                  </div>
                </fieldset>

                <Field label="Name" name="dataSourceId" value={metadata.dataSourceId} onChange={handleChange} readOnly={editMode} placeholder="e.g. product-guide" error={errors.dataSourceId} hint={editMode ? 'The source name and type cannot be changed.' : 'A unique name using letters, numbers, underscores or hyphens.'} />

                {sourceType === 'file' && <div className="space-y-5">
                  <div>
                    <p className="mb-2 text-sm font-medium text-gray-800">{editMode ? 'Replace document' : 'Document'}</p>
                    <label onDragOver={event => { event.preventDefault(); if (!processing) setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={event => { event.preventDefault(); setDragging(false); selectFile(event.dataTransfer.files[0]); }} className={`relative flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed px-5 py-7 text-center transition focus-within:ring-2 focus-within:ring-gray-400 ${dragging ? 'border-gray-900 bg-gray-100' : 'border-gray-200 bg-gray-50/70'}`}>
                      <input type="file" aria-label={editMode ? 'Replace document' : 'Choose document'} aria-invalid={Boolean(errors.file)} aria-describedby="file-help" accept={acceptedTypes.map(type => `.${type}`).join(',')} onChange={event => { selectFile(event.target.files[0]); event.target.value = ''; }} className="sr-only" />
                      {file ? <HiOutlineDocumentText className="mb-3 h-7 w-7 text-gray-600" /> : <HiOutlineCloudArrowUp className="mb-3 h-7 w-7 text-gray-400" />}
                      <span className="max-w-full break-all text-sm font-medium text-gray-800">{file ? file.name : 'Drop a document here, or browse'}</span>
                      <span className="mt-1 text-xs text-gray-500">{file ? `${(file.size / 1024).toFixed(1)} KB · Click to choose another file` : acceptedTypes.map(type => type.toUpperCase()).join(', ')}</span>
                    </label>
                    <p id="file-help" className={`mt-2 text-xs ${errors.file ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>{errors.file || (editMode && !file ? 'Your current document will be kept unless you upload a replacement.' : 'Upload one document per data source.')}</p>
                  </div>
                  <div className="rounded-xl border border-gray-200 p-4 space-y-3">
                    <label className="flex cursor-pointer items-center justify-between gap-4">
                      <span>
                        <span className="block text-sm font-medium text-gray-900">Read scanned documents</span>
                        <span className="mt-1 block text-xs leading-5 text-gray-500">Use OCR to extract text from scans and images.</span>
                      </span>
                      <input
                        type="checkbox"
                        checked={Boolean(metadata.ocr?.enable)}
                        onChange={event => update('ocr', { ...metadata.ocr, enable: event.target.checked, provider: metadata.ocr?.provider || 'default' })}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-400"
                      />
                    </label>
                    {metadata.ocr?.enable && (() => {
                      const selectedProvider = metadata.ocr?.provider || 'default';
                      const availableModels = modelsByProvider[selectedProvider] || [];
                      return (
                        <div className="pt-3 border-t border-gray-100 space-y-3">
                          <Field label="OCR Provider" name="ocrProvider">
                            <select
                              id="ocrProvider"
                              name="ocrProvider"
                              value={selectedProvider}
                              onChange={e => {
                                const newProvider = e.target.value;
                                const defaultModel = modelsByProvider[newProvider]?.[0]?.value || '';
                                update('ocr', {
                                  ...metadata.ocr,
                                  provider: newProvider,
                                  model: defaultModel,
                                });
                              }}
                              className={inputClass}
                            >
                              {OCR_PROVIDERS.map(p => (
                                <option key={p.value} value={p.value}>
                                  {p.label}
                                </option>
                              ))}
                            </select>
                          </Field>
                          {availableModels.length > 0 && (
                            <Field
                              label={`${selectedProvider === 'openai' ? 'OpenAI' : 'Gemini'} OCR Model`}
                              name="ocrModel"
                            >
                              <select
                                id="ocrModel"
                                name="ocrModel"
                                value={metadata.ocr?.model || availableModels[0]?.value}
                                onChange={e => update('ocr', { ...metadata.ocr, model: e.target.value })}
                                className={inputClass}
                              >
                                {availableModels.map(m => (
                                  <option key={m.value} value={m.value}>
                                    {m.name}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>}

                {sourceType === 'database' && <div className="space-y-4 rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">Connection details</h3>
                  <Field label="Database type" name="dbType" error={errors.dbType}><select id="dbType" name="dbType" value={metadata.dbType || ''} onChange={handleChange} aria-invalid={Boolean(errors.dbType)} aria-describedby={errors.dbType ? 'dbType-help' : undefined} className={inputClass}><option value="">Select a database</option><option value="postgresql">PostgreSQL</option><option value="mysql">MySQL</option></select></Field>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]"><Field label="Host" name="dbHost" value={metadata.dbHost || ''} onChange={handleChange} error={errors.dbHost} placeholder="db.example.com" /><Field label="Port" name="dbPort" type="number" value={metadata.dbPort || ''} onChange={handleChange} error={errors.dbPort} placeholder={metadata.dbType === 'mysql' ? '3306' : '5432'} /></div>
                  <Field label="Database name" name="dbName" value={metadata.dbName || ''} onChange={handleChange} error={errors.dbName} placeholder="Database name" />
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2"><Field label="Username" name="dbUser" value={metadata.dbUser || ''} onChange={handleChange} autoComplete="off" /><Field label="Password" name="dbPassword" type="password" value={metadata.dbPassword || ''} onChange={handleChange} autoComplete="new-password" /></div>
                </div>}

                {sourceType === 'web' && <div className="space-y-5">
                  <div className="space-y-3">
                    <div><h3 className="text-sm font-medium text-gray-900">Web pages</h3><p className="mt-1 text-xs text-gray-500">Add the URLs your agent should learn from.</p></div>
                    {urls.map((url, index) => <div key={index} className="flex items-center gap-2"><input type="url" aria-label={`Web page URL ${index + 1}`} aria-invalid={Boolean(errors.urls)} aria-describedby={errors.urls ? 'urls-error' : undefined} value={url} placeholder="https://example.com/help" onChange={event => update('urls', urls.map((value, i) => i === index ? event.target.value : value))} className={inputClass} /><button type="button" disabled={urls.length === 1} aria-label={`Remove URL ${index + 1}`} onClick={() => update('urls', urls.filter((_, i) => i !== index))} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-30 dark:hover:bg-red-950/30"><HiOutlineTrash className="h-4 w-4" /></button></div>)}
                    {errors.urls && <p id="urls-error" className="text-xs text-red-600 dark:text-red-400">{errors.urls}</p>}
                    <button type="button" onClick={() => update('urls', [...urls, ''])} className={secondaryButton}><HiOutlinePlus className="h-4 w-4" />Add URL</button>
                  </div>
                  <div className="space-y-4 rounded-xl border border-gray-200 p-4">
                    <div><h3 className="text-sm font-medium text-gray-900">Refresh schedule</h3><p className="mt-1 text-xs text-gray-500">Keep your knowledge up to date with recurring extraction.</p></div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Field label="Frequency" name="frequency"><select id="frequency" value={metadata.scheduler?.rateType || ''} onChange={event => update('scheduler', { ...metadata.scheduler, rateType: event.target.value })} className={inputClass}><option value="">Manual only</option><option value="hourly">Hourly</option><option value="daily">Daily</option></select></Field>
                      {metadata.scheduler?.rateType && <Field label={metadata.scheduler.rateType === 'daily' ? 'Every (days)' : 'Every (hours)'} name="schedule" type="number" min="1" step="1" value={metadata.scheduler.value ?? ''} onChange={event => { update('scheduler', { ...metadata.scheduler, value: event.target.value }); setErrors(previous => ({ ...previous, schedule: undefined })); }} error={errors.schedule} placeholder="1" />}
                    </div>
                    <button type="button" onClick={() => save(true)} className={secondaryButton}><HiOutlineArrowPath className="h-4 w-4" />Save & extract now</button>
                  </div>
                </div>}
              </fieldset>
            </div>
            <footer className="flex shrink-0 flex-wrap items-center justify-end gap-3 border-t border-gray-100 bg-gray-50/70 px-6 py-4">
              <span role="status" className="mr-auto text-xs text-gray-500">{progress}</span>
              <button type="button" onClick={close} disabled={processing} className={secondaryButton}>Cancel</button>
              <button type="submit" disabled={processing} className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-60">{processing ? <HiOutlineArrowPath className="h-4 w-4 animate-spin motion-reduce:animate-none" /> : <HiOutlineCheck className="h-4 w-4" />}{processing ? 'Saving…' : editMode ? 'Save changes' : 'Add data source'}</button>
            </footer>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
